// @flow
import React, {FunctionComponent} from 'react';
import {useHistory, useParams} from "react-router-dom";
import {Button, Divider, IconButton, List, ListItem, ListItemText, Stack, Typography} from "@mui/material";
import BreederList from "../Common/breeder-list";
import RenderOnRole from "../Permissions/render-on-role";
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import {RegistryDto$BreederRef as BreederRef} from "../../api/generated/registry-service";
import {RegistryService, RegistryUserType} from "../../services/registry-service";
import RouteService from "../../services/route-service";
import AuthService from "../../services/auth-service";
import DeleteIcon from "@mui/icons-material/Delete";
import {BreederSearchResult} from "../../api/generated/herd-breeder";
import {PANON_ADMIN, REGISTRY_WARDEN} from "../Permissions/Roles";
import {useQueryClient, useSuspenseQuery} from "@tanstack/react-query";
import FlexStack from "../Common/flex-stack";
import {Widget} from "../Common";

type Props = {};

interface RegistryAdministrationRouteParams {
    id: string
}

const refsToBreederListDtoPlaceholder = (breederRefs: BreederRef[]) => breederRefs.map(ref => ({
    id: ref.id,
    username: ref.name,
    email: "",
    herdCode: ref.herdCode,
    farmName: ref.farmName,
    website: '',
    owners: ref.owners ?? []
}));

const MembersList = ({members, handleRemoveMember}: {
    members: BreederRef[],
    handleRemoveMember?: (member: BreederRef) => void
}) =>
    <List>
        {members.flatMap(member => ([
                <ListItem key={member.id} secondaryAction={
                    handleRemoveMember && <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveMember(member)}>
                        <DeleteIcon/>
                    </IconButton>
                }>
                    <ListItemText primary={member.farmName} secondary={
                        <Stack>
                            <Typography variant="body2">{member.herdCode ? `Herdkürzel: ${member.herdCode}` : ''}</Typography>
                            {member.owners?.length > 0 && <Typography variant="body2">Besitzer: {member.owners.join(', ')}</Typography>}
                        </Stack>}/>
                </ListItem>,
                <Divider key={member.id + '-divider'} variant="middle" component="li"/>
            ])
        )}
    </List>;

const useRegistry = (registryId: string) => useSuspenseQuery({
    queryKey: ['registry', {registryId}],
    queryFn: async () => await RegistryService.loadById(registryId),
});

const useRegistryMembers = (registryId: string) => useSuspenseQuery({
    queryKey: ['registry', {registryId}, 'members'],
    queryFn: async () => await RegistryService.listMembers(registryId),
});

const RegistryAdministrationDetails: FunctionComponent<Props> = () => {
    const isWarden = AuthService.hasRole([REGISTRY_WARDEN, PANON_ADMIN]);
    const isAdmin = AuthService.hasRole([PANON_ADMIN]);

    const history = useHistory();
    const registryId = useParams<RegistryAdministrationRouteParams>().id;
    const [open, setOpen] = React.useState(false);
    const queryClient = useQueryClient();

    const {data: registry} = useRegistry(registryId);
    const {data: members} = useRegistryMembers(registryId);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleActionClick = (userType: RegistryUserType) => {
        handleClose();
        history.push(RouteService.expand(RouteService.REGISTRY_ADMINISTRATION_ADD_WILDCARD_USERTYPE, {id: registryId, userType: userType.toString()}));
    }

    const handleUnassignAdmin = isAdmin
        ? async (breeder: BreederSearchResult) => {
            if (window.confirm(`Administrator ${breeder.herdCode} ${breeder.farmName} aus Register entfernen?`)) {
                await RegistryService.unassignBreeder(registryId, breeder.id, RegistryUserType.ADMIN);
                await queryClient.invalidateQueries({queryKey: ['registry', {registryId}]});
            }
        }
        : undefined;

    const handleUnassignWarden = isAdmin
        ? async (breeder: BreederSearchResult) => {
            if (window.confirm(`Reviewer ${breeder.herdCode} ${breeder.farmName} aus Register entfernen?`)) {
                await RegistryService.unassignBreeder(registryId, breeder.id, RegistryUserType.WARDEN);
                await queryClient.invalidateQueries({queryKey: ['registry', {registryId}]});
            }
        }
        : undefined;

    const handleRemoveMember = isWarden
        ? async (member: BreederRef) => {
            if (window.confirm(`Mitglied ${member.herdCode} ${member.farmName} aus Register entfernen?`)) {
                await RegistryService.unassignBreeder(registryId, member.id, RegistryUserType.MEMBER);
                await queryClient.invalidateQueries({queryKey: ['registry', {registryId}]});
            }
        }
        : undefined;

    if (!registry) {
        return null;
    }

    return (
        <FlexStack>

            <Widget displayName={`Register: ${registry.name}`}>
                <RenderOnRole roles={[REGISTRY_WARDEN, PANON_ADMIN]}>
                    <Stack>
                        {isAdmin && <Button onClick={() => handleActionClick(RegistryUserType.ADMIN)} startIcon={<SupervisorAccountOutlinedIcon/>}>Administrator hinzufügen</Button>}
                        {isAdmin && <Button onClick={() => handleActionClick(RegistryUserType.WARDEN)} startIcon={<RateReviewOutlinedIcon/>}>Zuchtwart hinzufügen</Button>}
                        {isWarden && <Button onClick={() => handleActionClick(RegistryUserType.MEMBER)} startIcon={<PersonAddAlt1OutlinedIcon/>}>Züchter hinzufügen</Button>}
                    </Stack>
                </RenderOnRole>
            </Widget>

            <Widget displayName={`Administratoren (${registry.admins.length})`}
                    quickActions={isWarden ? [{
                        icon: <PersonAddAlt1OutlinedIcon/>,
                        action: () => handleActionClick(RegistryUserType.ADMIN)
                    }] : undefined}>
                {registry.admins.length > 0
                    ? <BreederList breeders={refsToBreederListDtoPlaceholder(registry.admins)}
                                   onListItemDelete={handleUnassignAdmin}/>
                    : <Typography align="center" variant="body1">Noch keine Administratoren vorhanden.</Typography>}
            </Widget>

            <Widget displayName={`Reviewer/Zuchtwarte (${registry.wardens.length})`}
                    quickActions={isAdmin ? [{
                        icon: <PersonAddAlt1OutlinedIcon/>,
                        action: () => handleActionClick(RegistryUserType.WARDEN)
                    }] : undefined}>
                {registry.wardens.length > 0
                    ? <BreederList breeders={refsToBreederListDtoPlaceholder(registry.wardens)} onListItemDelete={handleUnassignWarden}/>
                    : <Typography align="center" variant="body1">Noch keine Reviewer/Zuchtwarte vorhanden.</Typography>}
            </Widget>


            <Widget displayName={`Mitglieder/Züchter (${members.length})`}
                    quickActions={isAdmin ? [{
                        icon: <PersonAddAlt1OutlinedIcon/>,
                        action: () => handleActionClick(RegistryUserType.MEMBER)
                    }] : undefined}>
                <MembersList members={members} handleRemoveMember={handleRemoveMember}/>
            </Widget>

            <RenderOnRole roles={[REGISTRY_WARDEN, PANON_ADMIN]}>
                <SpeedDial
                    ariaLabel="SpeedDial tooltip example"
                    sx={{position: 'absolute', bottom: 16, right: 16}}
                    icon={<SpeedDialIcon/>}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    open={open}
                >
                    {isWarden && <SpeedDialAction
                        key="Mitglied"
                        icon={<PersonAddAlt1OutlinedIcon/>}
                        tooltipTitle="Mitglied"
                        tooltipOpen
                        onClick={() => handleActionClick(RegistryUserType.MEMBER)}
                    />}

                    {isAdmin && <SpeedDialAction
                        key="Reviewer"
                        icon={<RateReviewOutlinedIcon/>}
                        tooltipTitle="Reviewer/Zuchtwart"
                        tooltipOpen
                        onClick={() => handleActionClick(RegistryUserType.WARDEN)}
                    />}

                    {isAdmin && <SpeedDialAction
                        key="Administrator"
                        icon={<SupervisorAccountOutlinedIcon/>}
                        tooltipTitle="Administrator"
                        tooltipOpen
                        onClick={() => handleActionClick(RegistryUserType.ADMIN)}
                    />}

                </SpeedDial>
            </RenderOnRole>
        </FlexStack>
    );
};

export default RegistryAdministrationDetails
